import {
  CisRate,
  Color,
  ImageGallery,
  KeyValue,
  OfflineStock,
  OfflineStockResponse,
  PreSelection,
  SkuVariant,
  StockAvailability,
  StockDetails
} from '../types/product.types';
import { FLOW, Plan, PlanGroup } from '../types/plan.types';
import { api } from '@detox/actions';
import isFeatureFlagEnabled from './feature-flags';
import { ONLINE_STOCK_FROM_ESHOP } from '../types/featureFlag.types';
import { PRODUCT } from '../constants/product';
import {
  DeliveryOptions,
  IFulfilment,
  IVariant
} from '@common-modules/product-catalogue';
import { getUrlParams, setUrlParams } from '@lux/helpers';
import { updateUtmUrl } from './catalogue-helpers';
import { FulfilmentMode } from '../types/fulfilment.types';
import { SelectedPlan } from '../types/shoppingCart.types';
import { includes } from './common';
import CONSTANTS from '../constants/common';
import { trans as t } from '../helpers/localisation';
import slugify from 'slugify';
import { NetworkSpeedMap, planTagsConfig } from '../config/plan-group-config';

const fetchOnlineStock = api.onlineApi.eShopStockAvailability;

const fetchOfflineStock = api.aem.offlineStockAvailability;
const fetchMtposStockCache = api.onlineApi.eShopMtposStockAvailability;

const getColorHexCode = (colors: Color[] = [], colorName: string) => {
  return colors.find(color => color.name === colorName)?.hexCode;
};
const getImagesByColor = (
  colorCode: string,
  imageGallery: ImageGallery[] = [],
  title: string
) => {
  const filteredImages = imageGallery
    .find(gallery => gallery.name === colorCode)
    ?.images?.map(image => {
      return {
        imageFile: image,
        alt: `${title} ${colorCode}`
      };
    });
  return filteredImages || [];
};

export const getStockStatus = (
  availabilities: StockAvailability[] = [],
  deviceSkus: string[] = [],
  skusToCheck: string[] = []
) => {
  return skusToCheck.reduce(
    (result, sku) => {
      const availability = availabilities.find(item => item.itemCode === sku);
      if (
        !availability ||
        availability.status === PRODUCT.STOCK_STATUS.NOT_AVAILABLE ||
        availability.availableQty <= 0
      ) {
        result.unavailableItems.push({ sku });
        return result;
      }

      if (deviceSkus.includes(availability.itemCode)) {
        result.status = availability.availableQty > 0;
      }
      if (!availability.hasOnlineStock) {
        result.unavailableItems.push({ sku });
      }

      return result;
    },
    { status: false, unavailableItems: [] }
  );
};

export const getStockAvailability = async ({
  accessorySkus = [],
  freebieSkus = [],
  deviceSkus = [],
  groupId,
  checkStockFromStore,
  isMTPOSStockCacheEnabled
}: {
  deviceSkus: string[];
  groupId: string;
  checkStockFromStore?: boolean;
  isMTPOSStockCacheEnabled: boolean;
  accessorySkus: string[];
  freebieSkus: string[];
}): Promise<StockDetails> => {
  const skus = [...deviceSkus, ...accessorySkus, ...freebieSkus];
  const stockAvailability = await checkStock(
    skus,
    groupId,
    checkStockFromStore,
    isMTPOSStockCacheEnabled,
    deviceSkus
  );
  const accessories = stockAvailability.onlineStocks.filter(stock =>
    accessorySkus.includes(stock.itemCode)
  );

  const accessoriesWarehouseStock = stockAvailability.onlineStocks.filter(
    stock => accessorySkus.includes(stock.itemCode) && stock.hasOnlineStock
  );

  const freebies = stockAvailability.onlineStocks.filter(stock =>
    freebieSkus.includes(stock.itemCode)
  );

  const devices = stockAvailability.stock.filter(stock =>
    deviceSkus.includes(stock.itemCode)
  );

  const storeStock = stockAvailability.storeStock.filter(stock =>
    deviceSkus.includes(stock.itemCode)
  );

  const warehouseStock = stockAvailability.onlineStocks.filter(stock =>
    deviceSkus.includes(stock.itemCode)
  );

  const { status, unavailableItems } = getStockStatus(
    [...freebies, ...devices, ...accessories],
    deviceSkus,
    skus
  );

  return {
    accessories: { stock: accessoriesWarehouseStock },
    freebies: { stock: freebies },
    devices: { stock: devices, storeStock, status, warehouseStock },
    unavailableItems,
    error: stockAvailability.status === false
  };
};

const getDeviceVariants = ({
  skuVariants = [],
  plans = [],
  colors = [],
  seoImageTag,
  imageGallery,
  groupId,
  tags = [],
  brand,
  sellingFastThreshold,
  productTitle,
  stockAvailability = [],
  stockStatus
}: {
  skuVariants: SkuVariant[];
  plans?: Plan[];
  colors: Color[];
  seoImageTag: string;
  imageGallery: ImageGallery[];
  groupId?: string;
  tags?: string[];
  brand?: string;
  sellingFastThreshold?: number;
  productTitle?: string;
  stockAvailability?: StockAvailability[];
  stockStatus?: boolean;
}): { status: boolean; variants: IVariant[] } => {
  const formattedVariants = skuVariants.map(skuVariant => {
    const planGroup = plans.find(plan => plan.mecId === skuVariant.planId)
      ?.groupName;
    const stock = stockAvailability?.find(
      stock => stock.itemCode === skuVariant.sku
    );

    const stockCount =
      stock?.availableQty && stock?.availableQty > 0 ? stock?.availableQty : 0;
    const onlineStock = stock?.hasOnlineStock;
    const offlineStock = stock?.hasOfflineStock;

    return {
      ...skuVariant,
      isBackOrder: skuVariant.isBackOrder,
      deviceId: skuVariant.productId,
      colorCode: skuVariant.colour,
      colorHexCode: getColorHexCode(colors, skuVariant.colour),
      deviceCode: skuVariant.sku,
      deviceDescription: '',
      modelCode: skuVariant.productId,
      modelDescription: '',
      memorySize: skuVariant.size,
      colorDescription: skuVariant.colour,
      msaHeaderId: '',
      planId: skuVariant.planId,
      price: skuVariant.price,
      discountPrice: 0,
      stockCount,
      groupId,
      images: getImagesByColor(skuVariant.colour, imageGallery, seoImageTag),
      planGroup,
      hasOfflineStock: offlineStock,
      hasOnlineStock: onlineStock,
      popStationAvailable: tags.includes(PRODUCT.TAGS.POP_STATION),
      brand,
      seoImageTag: seoImageTag,
      deviceTitle: productTitle,
      installmentPrice:
        plans?.length || skuVariant.monthlyTerm
          ? skuVariant.installmentPrice
          : 0,
      storageHighlightText:
        sellingFastThreshold &&
        stockCount &&
        stockCount <= sellingFastThreshold &&
        t('TEXT_SELLING_FAST')
    };
  });
  return { status: stockStatus, variants: formattedVariants };
};

const getPlanGroups = (
  allPlanGroup: PlanGroup[],
  plans: Plan[],
  flow: FLOW
): PlanGroup[] => {
  const uniqueAvailableGroup = [...new Set(plans.map(plan => plan.groupName))];
  return allPlanGroup.filter(
    group =>
      uniqueAvailableGroup.includes(group.groupName) &&
      group?.segment?.includes(flow)
  );
};

export const getPlanTagConfig = (
  planTags: string[],
  networkSpeedMapConfig: string = NetworkSpeedMap
): { tagText: string; tagBgColor: string } => {
  const def = {
    tagText: '4G',
    tagBgColor: '#222222'
  };

  if (!planTags || !Array.isArray(planTags)) {
    return def;
  }

  let networkSpeedTag = '';
  const networkSpeeds = planTags
    .filter(tag => tag.toLowerCase().includes('network-speed'))
    .map(tag => {
      const match = tag.match(/network-speed\/([^/]+)/i);
      return match ? match[1] : null;
    })
    .filter(Boolean);

  networkSpeedTag =
    networkSpeeds.length > 0 ? networkSpeeds[networkSpeeds.length - 1] : '';

  if (networkSpeedTag) {
    const tagConfig = networkSpeedMapConfig
      .split(',')
      .find(item => item.startsWith(`${networkSpeedTag}:`));

    return tagConfig
      ? {
          tagText: tagConfig.split(':')[1] || '5G+',
          tagBgColor: tagConfig.split(':')[2] || '#222222'
        }
      : def;
  }

  return def;
};

export const getPlanTag = (planTags: string[]): string => {
  const is5g = planTags?.includes(planTagsConfig['5g-speed'].tagName);

  switch (true) {
    case is5g: {
      return '5G+';
    }
    default: {
      return '4G';
    }
  }
};

const getPlans = (
  plans: Plan[],
  cisRates?: CisRate[],
  networkSpeedMap: string = NetworkSpeedMap,
  defaultPlanIds: string[] = []
) => {
  return plans.map(plan => {
    const returnPlan = {
      ...plan,
      displayName: plan.planName,
      tag: getPlanTag(plan.tags),
      tagConfig: getPlanTagConfig(plan.tags, networkSpeedMap),
      defaultSelected: defaultPlanIds.length
        ? defaultPlanIds.includes(plan.planId)
        : plan.selected
    };
    if (!cisRates) {
      return returnPlan;
    }
    const matchedCisRate = cisRates.find(
      cisRate => cisRate.planID === plan.basePriceSchemaId
    );
    const monthlyCharges = Number(plan.monthlyCharges);
    const discountedPrice = Number(matchedCisRate?.discountedPrice);
    if (monthlyCharges === discountedPrice) {
      return returnPlan;
    }
    return {
      ...returnPlan,
      discountedPrice
    };
  });
};

export const getProductSpecsWithoutStorage = (
  specString: string,
  colours?: Color[],
  isRRP?: boolean
) => {
  let color = null,
    planInfo = null;
  const urlColor = colours?.find(colour =>
    specString.includes(slugify(colour?.name.toLowerCase()))
  )?.name;
  if (urlColor) {
    const selectedColor = slugify(urlColor.toLowerCase());
    const splitInfoByColor = specString.split(selectedColor + '-');
    color = selectedColor;
    const planRegex = splitInfoByColor[1]?.split('-') ?? [];
    const hasWrongStorage = /\d/.test(planRegex[0]);
    if (hasWrongStorage && !isRRP) {
      planInfo = planRegex.slice(1);
    } else {
      planInfo = planRegex;
    }
  }

  return {
    colorValue: color,
    planInfoValue: planInfo
  };
};

const getProductSpecsFromUrl = (): PreSelection => {
  if (typeof window === 'undefined') {
    return {};
  }
  const urlParams = getUrlParams();
  return {
    color: urlParams[CONSTANTS.URL_PARAMS.COLOR],
    storage: urlParams[CONSTANTS.URL_PARAMS.SIZE],
    plan: urlParams[CONSTANTS.URL_PARAMS.PLAN],
    monthlyTerm: urlParams[CONSTANTS.URL_PARAMS.MONTHLY_TERM],
    paymentTerm: urlParams[CONSTANTS.URL_PARAMS.PAYMENT_TERM] ?? 'fullprice'
  };
};

const setUrlAfterChangeVariant = (
  variant: KeyValue,
  slug: string,
  locationStr: string,
  isRRP = false
) => {
  const utmParams = updateUtmUrl(locationStr);
  const { colorCode, planName, memorySize, monthlyTerm, paymentTerm } = variant;
  window.history.replaceState(
    null,
    null,
    setUrlParams(
      `/${
        isRRP ? CONSTANTS.URL_PATHS.RRP_PHONE : CONSTANTS.URL_PATHS.PHONE
      }/${slug}`,
      {
        ...utmParams,
        ...constructUrlParams({
          [CONSTANTS.URL_PARAMS.COLOR]: colorCode,
          [CONSTANTS.URL_PARAMS.PLAN]: planName,
          [CONSTANTS.URL_PARAMS.SIZE]: memorySize,
          [CONSTANTS.URL_PARAMS.MONTHLY_TERM]: isRRP ? undefined : monthlyTerm,
          [CONSTANTS.URL_PARAMS.PAYMENT_TERM]: paymentTerm
        })
      }
    )
  );
};

const filterOfflineStocks = (
  offlineStocks: OfflineStock[] = [],
  skus: string[] = []
): StockAvailability[] => {
  return skus.map(skuId => {
    const matchedSku = offlineStocks.find(
      offlineStock => offlineStock.skuId === skuId
    );
    const skuHasStock = matchedSku?.data?.find(
      stockAvailability =>
        PRODUCT.STOCK_AVAILABLE_STATUS.includes(
          stockAvailability.stock_status
        ) &&
        Number(stockAvailability.available_quantity) > 0 &&
        stockAvailability.store_id !== PRODUCT.STORE_ID.WARE_HOUSE
    );
    return {
      itemCode: skuId,
      status: skuHasStock
        ? PRODUCT.STOCK_STATUS.AVAILABLE
        : PRODUCT.STOCK_STATUS.NOT_AVAILABLE,
      availableQty: Number(skuHasStock?.available_quantity) || 0
    };
  }, []);
};

const mergeOfflineAndOnlineStock = (
  offlineStocks: StockAvailability[] = [],
  onlineStocks: StockAvailability[] = []
): StockAvailability[] => {
  return onlineStocks.map(onlineStock => {
    const offlineStock = offlineStocks.find(
      offlineStock => offlineStock.itemCode === onlineStock.itemCode
    );
    const hasOfflineStock = offlineStock?.availableQty > 0;
    const hasOnlineStock = onlineStock.availableQty > 0;
    return {
      ...offlineStock,
      hasOfflineStock,
      hasOnlineStock,
      availableQty: offlineStock?.availableQty || onlineStock.availableQty || 0,
      status:
        hasOfflineStock || hasOnlineStock
          ? PRODUCT.STOCK_STATUS.AVAILABLE
          : PRODUCT.STOCK_STATUS.NOT_AVAILABLE
    };
  });
};

const getOnlineStockFromWarehouse = (
  offlineStocks: OfflineStock[] = [],
  skus: string[] = []
): StockAvailability[] => {
  return skus.map(skuId => {
    const matchedSku = offlineStocks
      .find(offlineStock => offlineStock.skuId === skuId)
      ?.data?.find(
        stockAvailability =>
          stockAvailability.store_id === PRODUCT.STORE_ID.WARE_HOUSE
      );
    const availableQty =
      (PRODUCT.STOCK_AVAILABLE_STATUS.includes(matchedSku?.stock_status) &&
        Number(matchedSku?.available_quantity)) ||
      0;
    return {
      itemCode: skuId,
      availableQty,
      status:
        availableQty > 0
          ? PRODUCT.STOCK_STATUS.AVAILABLE
          : PRODUCT.STOCK_STATUS.NOT_AVAILABLE
    };
  });
};

const getOfflineStock = async (groupId): Promise<OfflineStockResponse[]> => {
  if (!groupId) return [];
  try {
    return await fetchOfflineStock(groupId);
  } catch (e) {
    return [];
  }
};

const formatEshopMtposAvailability = (
  eshopMtposAvailability: StockAvailability[],
  isStoreStock = false
): StockAvailability[] => {
  return eshopMtposAvailability.map(stock => {
    if (stock.availableQty) {
      const availableQty =
        stock?.availableQty && stock?.availableQty > 0
          ? stock?.availableQty
          : 0;
      return {
        ...stock,
        availableQty,
        hasOnlineStock: !!(
          stock.hasOnlineStock ??
          (!isStoreStock && availableQty > 0)
        ),
        hasOfflineStock: !!(
          stock.hasOfflineStock ??
          (isStoreStock && availableQty > 0)
        )
      };
    }
    const stockAvailable = stock.available || stock.isAvailable;
    return {
      itemCode: stock.itemCode,
      availableQty: stockAvailable ? 1 : 0,
      status: stockAvailable
        ? CONSTANTS.STOCK_STATUS.AVAILABLE
        : CONSTANTS.STOCK_STATUS.NOT_AVAILABLE,
      hasOnlineStock: !!stock?.availableInWarehouse,
      hasOfflineStock: !!(
        stock?.availableInStore ??
        (isStoreStock && stockAvailable)
      )
    };
  });
};

const checkStock = async (
  skus: string[],
  groupId?: string,
  checkStockFromStore = true,
  isMTPOSStockCacheEnabled = false,
  deviceSkus: string[] = []
): Promise<{
  status: boolean;
  stock: StockAvailability[];
  onlineStocks: StockAvailability[];
  storeStock: StockAvailability[];
}> => {
  try {
    let stockList,
      onlineStocks = [],
      offLineStocks = [],
      storeStock = [];
    const uniqueSkus = Array.from(new Set(skus));
    if (isMTPOSStockCacheEnabled) {
      onlineStocks = await fetchMtposStockCache(uniqueSkus);
      if (!Array.isArray(onlineStocks)) {
        onlineStocks = [];
      }
      stockList = onlineStocks;
    } else {
      const shouldCheckOnlineStockFromEShop = isFeatureFlagEnabled(
        ONLINE_STOCK_FROM_ESHOP
      );
      if (shouldCheckOnlineStockFromEShop) {
        offLineStocks = (await getOfflineStock(groupId)) || [];
        onlineStocks = getOnlineStockFromWarehouse(
          offLineStocks[0]?.items,
          uniqueSkus
        );
      } else {
        [onlineStocks = [], storeStock = []] = await Promise.all([
          fetchOnlineStock(uniqueSkus),
          checkStockFromStore && deviceSkus.length
            ? fetchOnlineStock(deviceSkus, null, 'store')
            : []
        ]);
      }
      if (!Array.isArray(onlineStocks)) {
        onlineStocks = [];
      }

      stockList = storeStock.length
        ? mergeOfflineAndOnlineStock(
            formatEshopMtposAvailability(storeStock),
            onlineStocks
          )
        : onlineStocks;
    }

    return {
      status: true,
      stock: formatEshopMtposAvailability(stockList),
      onlineStocks: formatEshopMtposAvailability(onlineStocks),
      storeStock: formatEshopMtposAvailability(storeStock, true)
    };
  } catch (e) {
    return { status: false, stock: [], onlineStocks: [], storeStock: [] };
  }
};

const getDeliveryOptions = (
  preOrderLinerForProductDetails: string,
  backOrderLinerForProductDetails: string,
  tags: string[],
  fulfilment: FulfilmentMode[] = [],
  extraLiner = '',
  rrp = false
): DeliveryOptions => {
  const fulfilmentList = fulfilment.map<IFulfilment>(fulfilment => {
    const disabled =
      fulfilment.fulfilmentId === PRODUCT.FULFILMENT_TYPES.POP_STATION &&
      fulfilment.tags?.length &&
      !tags.includes(fulfilment.tags[0]);
    return {
      type: fulfilment.fulfilmentId,
      cost: fulfilment.mobileFulfilment.price,
      title: fulfilment.mobileFulfilment.title,
      liner: fulfilment.mobileFulfilment.liner,
      disabled
    };
  });

  return {
    preOrderLiner: preOrderLinerForProductDetails,
    backOrderLiner: backOrderLinerForProductDetails,
    fulfilmentList,
    extraLiner,
    rrp
  };
};

const updateAccessories = (formattedItems, selectedItems) => {
  if (!formattedItems) return;
  const updatedAccessories = formattedItems.map(formattedItem => {
    const preselected = selectedItems.find(
      selectedItem => selectedItem.deviceId === formattedItem.deviceId
    );
    if (preselected) {
      return { ...formattedItem, selectedVariant: preselected };
    }
    return formattedItem;
  });
  return updatedAccessories;
};

const getPromotions = ({
  selectedPlan,
  promotions,
  isCIS
}: {
  selectedPlan: SelectedPlan;
  promotions: KeyValue;
  isCIS: boolean;
}) => {
  const phonePromotion = isCIS ? promotions?.cis : promotions?.res;

  const promoDescription = phonePromotion?.promoDescriptions?.find(desc => {
    return desc.planGroups === selectedPlan?.groupName;
  });

  if (!promoDescription) return null;

  const filteredPromotions = phonePromotion?.promotions?.reduce(
    (result, promotion) => {
      const filteredPromos = promotion.promos?.filter(promo => {
        return (
          promo.planGroups?.length === 0 ||
          includes(promo.planGroups, selectedPlan?.groupName)
        );
      });
      if (filteredPromos.length) {
        result.push({
          ...promotion,
          promos: filteredPromos
        });
      }

      return result;
    },
    []
  );

  return {
    productId: phonePromotion?.productId,
    ...promoDescription,
    promotions: filteredPromotions
  };
};

export const constructUrlParams = (object: KeyValue) => {
  const result = {};
  for (const key in object) {
    if (object[key] !== undefined) {
      result[key] = slugify(object[key], { lower: true });
    }
  }
  return result;
};

export const checkDefaultVariantChanged = (
  defaultVariant: Partial<IVariant & KeyValue>,
  newVariant: Partial<IVariant & KeyValue>
) => {
  return (
    newVariant.colorCode !== defaultVariant?.colorCode ||
    newVariant.memorySize !== defaultVariant?.memorySize ||
    newVariant.planId !== defaultVariant?.planId ||
    newVariant.paymentTerm !== defaultVariant?.paymentTerm ||
    newVariant.monthlyTerm !== defaultVariant?.monthlyTerm
  );
};

export const getDefaultPlan = (
  defaultPlanIds: string[] = [],
  plans: Plan[] = []
) => {
  return (
    plans.find(plan => {
      return defaultPlanIds.includes(plan.planId);
    }) || plans[0]
  );
};

export {
  getDeviceVariants,
  getColorHexCode,
  getImagesByColor,
  getPlanGroups,
  getPlans,
  getProductSpecsFromUrl,
  setUrlAfterChangeVariant,
  checkStock,
  getDeliveryOptions,
  updateAccessories,
  getPromotions
};
